import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { SlotStates } from "../../api/models/ScheduleSlotModel";
import ScheduleCalendar from "../../components/calendar/ScheduleCalendar";
import DropDown from "../../components/forms/dropdown/DropDown";
import useCalendarViewDate from "../../hooks/useCalendarViewDate";
import CalendarScheduleModel from "../../models/CalendarScheduleModel";
import AppButton from "../../components/buttons/AppButton";
import CalendarScheduleSlotModel from "../../models/CalendarScheduleSlotModel";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useAvailabilityTutorSchedule } from "../../hooks/useAvailabilitySchedule";
import { indexTutorSchedules } from "../../api/endpoints/tutors/schedules";
import SchedulingService from "../../services/schedulingService";
import CalendarScheduleRequest from "../../api/models/CalendarScheduleRequest";
import { ToastContext } from "../../App";
import StudentModel from "../../api/models/StudentModel";
import { tutorCancelGroup } from "../../api/endpoints/tutors/sessions";
type ChangeTutorAppointments = {
  student: StudentModel;
  setOpen: () => void;
  selectedSlots: CalendarScheduleSlotModel[];
  setSelectedSlots: Dispatch<SetStateAction<CalendarScheduleSlotModel[]>>;
  handleSubmit: () => void;
};
export default function ChangeTutorAppointments(
  props: ChangeTutorAppointments
) {
  const { student, selectedSlots, setSelectedSlots, setOpen, handleSubmit } =
    props;
  const LocalBasePath = "page.candidate.schedule";
  const { t, i18n } = useTranslation("", { keyPrefix: LocalBasePath });
  const authContext = useAuth();
  const navigate = useNavigate();
  const [availabilityTutorSchedule] = useAvailabilityTutorSchedule();
  const [, updateState] = useState<Object>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const [selectedStudent, setSelectedStudent] = useState<
    number | string | null | undefined
  >(student!.id);
  const { viewingDate, onChangeViewingDate } = useCalendarViewDate();
  const [schedule, setSchedule] = useState<object>({});
  const toast = useContext(ToastContext);
  const [selectedSessions, setSelectedSessions] =
    useState<CalendarScheduleModel | null>(null);
  const [isDone, setIsDone] = useState(false);
  const getUserSchedule: any = async () => {
    const UserScheduleApi = await indexTutorSchedules({
      filters: { student_id: selectedStudent, view: "tutor_reschedule" },
    });
    if (UserScheduleApi.isSuccess) {
      return UserScheduleApi.data;
    }
  };
  useEffect(() => {
    fetchAndSetSchedule();
  }, []);

  const fetchAndSetSchedule = async () => {
    let fetchedSchedule = await getUserSchedule();
    setSchedule(fetchedSchedule);
  };
  useEffect(() => {
    fetchAndSetSchedule();
  }, [selectedStudent]);

  const mergeSchedules = () => {
    const calendarAvailabilitySchedule = new CalendarScheduleModel(
      availabilityTutorSchedule,
      SlotStates.available
    );
    let calendarRecitationSchedule = new CalendarScheduleModel(
      schedule,
      SlotStates.available
    );
    calendarRecitationSchedule.slots = calendarRecitationSchedule.slots.filter(
      (slot) => !slot.isCandidateSlot
    );

    /**
     * The order of spreading schedules is important as we may have duplication or different slots with the same start and end time
     * The last added slots will override the first slots in the UI if there is duplication
     * So, we give here the higher priority to the recitation schedule
     */
    let calendarSchedule = new CalendarScheduleModel({});
    const filterScheduleSlots = calendarRecitationSchedule?.slots
      ?.map((slotRecitationSchedule) => {
        const hasSlot = calendarAvailabilitySchedule.slots?.filter(
          (slotAvailabilitySchedule) =>
            slotRecitationSchedule?.startTime.format("HH:mm") ===
              slotAvailabilitySchedule?.startTime.format("HH:mm") &&
            slotAvailabilitySchedule?.dayOfWeek ===
              slotRecitationSchedule?.dayOfWeek
        );

        if (hasSlot.length) {
          return slotRecitationSchedule;
        }
      })
      .filter((value) => typeof value !== "undefined");

    calendarSchedule.slots = [
      ...calendarAvailabilitySchedule.slots,
      ...filterScheduleSlots,
    ] as CalendarScheduleSlotModel[];
    setSelectedSessions(calendarSchedule);
  };
  useEffect(() => {
    if (!!schedule) mergeSchedules();
  }, [schedule]);

  const tutorCancelGroups: any = async () => {
    const GroupCanceledApi = await tutorCancelGroup();
    if (GroupCanceledApi.isSuccess) {
      return resetForm();
    }
  };
  const onSlotClick = (slot: any) => {
    let finalSelected: any = [];
    const getStudent = student;
    let schedulingService = new SchedulingService(
      selectedSessions!,
      getStudent!
    );

    schedulingService.toggleOwnSlot(slot);
    setSelectedSessions(schedulingService.schedule);
    finalSelected = schedulingService.schedule.slots;
    let selected: any = finalSelected.map((slot: any) => {
      if (slot?.state === 2) return slot;
    });
    setSelectedSlots(selected);
    forceUpdate();
  };

  const studentsOptions = [
    {
      key: student.name,
      value: `${student.id}`,
    },
  ];
  useEffect(() => {
    setSelectedStudent(studentsOptions[0].value);
  }, []);

  const handleCloseModal = () => {
    setIsDone(false);
    navigate("/tutor/dashboard/appointments-requests/true");
  };
  const resetForm = () => {
    setSelectedSlots([]);
    setOpen();
  };
  return (
    <div className="flex relative  flex-col w-screen h-screen  divide-y divide-gray-200">
      <div className="flex flex-col justify-between gap-5 p-6 pb-4">
        <h2 className="text-lg font-semibold leading-8 text-gray-900">
          {t("setNewAppointments")}
        </h2>
        <DropDown
          disabled={true}
          name={t("student")}
          setValue={(val: any) => setSelectedStudent(val)}
          defaultValue={selectedStudent}
          label={t("student")}
          placeholder={t("chooseStudent")}
          error={false}
          options={studentsOptions}
          className="w-full"
        />
        <p className="text-xs font-medium text-gray-400">{t("disclaimer")}</p>
      </div>
      <div className="w-full overflow-y-auto">
        <ScheduleCalendar
          selectedDate={viewingDate}
          viewingDate={viewingDate}
          schedule={selectedSessions}
          onChangeViewingDate={onChangeViewingDate}
          onSlotClick={onSlotClick}
          compact={true}
          showDates={false}
          highlightSelectedDate={false}
        />
      </div>

      <div className="w-full z-20  sticky    bottom-0   bg-white  flex border-t border-gray-200  px-6 py-4 justify-end items-center">
        <button
          type="button"
          className="rounded-md border mx-1 border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
          onClick={() => tutorCancelGroups()}
        >
          {t(`cancel`)}
        </button>
        {!!selectedSlots.length && (
          <button
            onClick={() => handleSubmit()}
            type="submit"
            className="mx-4 inline-flex justify-center rounded-md border border-transparent bg-brandblue-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-brandblue-700 focus:outline-none focus:ring-2 focus:ring-brandblue-500 focus:ring-offset-2"
          >
            {t(`send`)}
          </button>
        )}
      </div>
      <ConfirmationModal
        isOpen={isDone}
        btnText="requestPreview"
        onClose={handleCloseModal}
        email={authContext?.currentUser?.email || ""}
      />
    </div>
  );
}
